'use client';

import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import * as yup from 'yup';
import { Button } from '@repo/ux/button';
import { Calendar } from '@repo/ux/calendar';
import { Input } from '@repo/ux/input';
import { Label } from '@repo/ux/label';
import { cn } from '@repo/ux/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ux/popover';

function formatDate(date: Date | undefined) {
  if (!date) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Applies mm/dd/yyyy mask to raw digit characters.
 * Auto-prefixes 0 when the tens digit is impossible (month > 1, day > 3).
 */
function maskDateInput(digits: string): string {
  if (!digits) return '';
  const d = digits.slice(0, 8);
  let i = 0;

  // Month (01–12)
  let month = '';
  if (i < d.length) {
    if (parseInt(d[i], 10) > 1) {
      month = '0' + d[i++]; // e.g. '9' → '09'
    } else {
      month += d[i++];
      if (i < d.length) month += d[i++];
    }
  }

  // Day (01–31) — only once month is complete (2 digits)
  let day = '';
  if (month.length === 2 && i < d.length) {
    if (parseInt(d[i], 10) > 3) {
      day = '0' + d[i++]; // e.g. '9' → '09'
    } else {
      day += d[i++];
      if (i < d.length) day += d[i++];
    }
  }

  // Year (4 digits) — only once day is complete
  let year = '';
  if (day.length === 2 && i < d.length) {
    year = d.slice(i, i + 4);
  }

  let result = month;
  if (day) result += '/' + day;
  if (year) result += '/' + year;
  return result;
}

/**
 * Parses a fully-entered mm/dd/yyyy string and returns a UTC Date,
 * or null if the string is incomplete or represents an invalid calendar date.
 */
function parseDateString(formatted: string): Date | null {
  if (formatted.length !== 10) return null;
  const parts = formatted.split('/');
  if (parts.length !== 3) return null;
  const [mm, dd, yyyy] = parts.map(Number);
  if (
    !mm ||
    !dd ||
    !yyyy ||
    mm < 1 ||
    mm > 12 ||
    dd < 1 ||
    dd > 31 ||
    yyyy < 1000
  )
    return null;
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));
  // Catches e.g. Feb 31 rolling over into March
  if (date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) return null;
  return date;
}

export const birthdateSchema = yup
  .date()
  .typeError('Date is invalid')
  .required('Birthdate is required')
  .max(new Date(), 'Date of birth must be in the past');

export function DatePicker({
  label = 'Date of Birth',
  className,
  value: initialValue,
  onChange,
  errors,
  disabled = false,
}: {
  label?: string;
  className?: string;
  value?: Date | null;
  onChange?: (date: Date | undefined | null) => void;
  errors?: { message: string } | undefined;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(initialValue || undefined);
  const [month, setMonth] = useState<Date | undefined>(date);
  const [value, setValue] = useState(formatDate(date));

  return (
    <div className="flex flex-col gap-3">
      <Label htmlFor="date" className="px-1">
        {label}
      </Label>
      <div className="relative flex gap-2">
        <Input
          id="date"
          autoComplete="off"
          value={value}
          placeholder="mm/dd/yyyy"
          disabled={disabled}
          className={cn(
            'bg-background rounded-lg border-gray-200 px-4 py-3 transition-all duration-200 text-form-input h-auto focus:border-spkblue-base_color focus-visible:ring-spkblue-base_color focus-visible:ring-[1px] disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400',
            className
          )}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            const formatted = maskDateInput(digits);
            setValue(formatted);
            const parsed = parseDateString(formatted);
            if (parsed) {
              setDate(parsed);
              setMonth(parsed);
              onChange?.(parsed);
            } else if (!formatted) {
              setDate(undefined);
              onChange?.(null);
            } else {
              // Non-empty but invalid (e.g. 03/33/1970): send an invalid Date
              // so Yup's typeError fires rather than the "required" message.
              onChange?.(new Date(NaN));
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />

        <Popover
          open={open}
          onOpenChange={(val) => {
            if (!disabled) {
              setOpen(val);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              id="date-picker"
              variant="ghost"
              className={cn(
                'absolute top-1/2 right-2 size-6 -translate-y-1/2 hover:cursor-pointer',
                { 'opacity-50': disabled }
              )}
            >
              <CalendarIcon className="size-5" />
              <span className="sr-only">Select date</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              month={month}
              onMonthChange={setMonth}
              onSelect={(date) => {
                setDate(date);
                setValue(formatDate(date));
                setOpen(false);
                onChange?.(date);
              }}
              className="[--cell-size:--spacing(11)] md:[--cell-size:--spacing(13)]"
            />
          </PopoverContent>
        </Popover>
      </div>
      {errors && (
        <p
          className="text-sm text-red-600"
          id="hs-validation-name-error-helper"
        >
          {errors.message}
        </p>
      )}
    </div>
  );
}
